<template>
  <div>
    <div v-if="blocksWithIteratedChapter" class="container">
      <ChapterNav :blocks="blocksWithIteratedChapter" :title="title" class="max-w-screen-md mx-auto" />
    </div>
    <div v-for="(block, index) in blocks" :key="index">
      <ProviderWrapper :block-index="index">
        <component
          :is="block.typeHandle"
          v-if="isVisible(block)"
          v-bind="{
            description: product?.descriptionHtml,
            product,
            ...block, // Overwrite all other props with block props
            blockPosition: index,
            socialMediaAccounts,
            addOn,
            youtubeId: product?.video,
            handles: product?.relatedProducts,
            handle: product?.bundleProduct,
            features: product?.keyFeatures,
            agreementCategory: block.agreementCategory?.length ? block.agreementCategory : agreementCategory,
            gridBlocks,
            collection,
            entryId,
            productReference: entry?.productReference,
          }"
        />
      </ProviderWrapper>
    </div>
  </div>
</template>

<script>
// #region imports
import Accordion from '@/components/common/blocks/Accordion';
import AddOnTrainingChapter from '@/components/common/blocks/AddOnTrainingChapter';
import AlsoBought from '@/components/common/blocks/AlsoBought';
import AppCta from '@/components/common/blocks/AppCta';
import ArticleChapter from '@/components/common/blocks/ArticleChapter';
import ArticleRowSlide from '@/components/common/blocks/ArticleRowSlide';
import AsSeenIn from '@/components/common/blocks/AsSeenIn';
import CampaignGrid from '@/components/common/blocks/CampaignGrid';
import CenteredContent from '@/components/common/blocks/CenteredContent';
import ChapterNav from '@/components/common/blocks/ChapterNav';
import CollectionInformation from '@/components/common/blocks/CollectionInformation';
import LinksDisplay from '~/components/common/blocks/LinksDisplay';
import SectionHeading from '~/components/common/blocks/SectionHeading';
import CompareContentSlider from '@/components/common/blocks/CompareContentSlider';
import CompareTable from '@/components/common/blocks/CompareTable';
import ContactBlock from '@/components/common/blocks/ContactBlock';
import ContentList from '@/components/common/blocks/ContentList';
import ContentRecommendationSlide from '@/components/common/blocks/ContentRecommendationSlide';
import ContentWithIcons from '@/components/common/blocks/ContentWithIcons';
import ConversionAreaOne from '@/components/common/blocks/ConversionAreaOne';
import CopyText from '@/components/common/blocks/CopyText';
import CourseContent from '@/components/common/blocks/CourseContent';
import CourseConversionBanner from '@/components/common/blocks/CourseConversionBanner';
import CrossSell from '@/components/common/blocks/CrossSell';
import Exercise from '@/components/common/blocks/Exercise';
import ExerciseRoutineCardsSliderWithText from '@/components/common/blocks/ExerciseRoutineCardsSliderWithText';
import ExpertDetailsListing from '@/components/common/blocks/ExpertDetailsListing';
import ExploreMore from '@/components/common/blocks/ExploreMore';
import FeaturedArticlesSlide from '@/components/common/blocks/FeaturedArticlesSlide';
import FeaturedExperts from '@/components/common/blocks/FeaturedExperts';
import FeatureList from '@/components/common/blocks/FeatureList';
import FeaturesRotation from '@/components/common/blocks/FeaturesRotation';
import FeaturesWithIcons from '@/components/common/blocks/FeaturesWithIcons';
import FiftyFiftyTeaser from '~/components/common/blocks/FiftyFiftyTeaser';
import FindTraining from '@/components/common/blocks/FindTraining';
import FixedBoxImages from '@/components/common/blocks/FixedBoxImages';
import FixedBoxTopicFeature from '@/components/common/blocks/FixedBoxTopicFeature';
import FormLink from '@/components/common/blocks/FormLink';
import FullWidthAutoplayVideo from '@/components/common/blocks/FullWidthAutoplayVideo';
import FullWidthBrand from '@/components/common/blocks/FullWidthBrand';
import FullWidthTeaser from '~/components/common/blocks/FullWidthTeaser';
import FullwidthTopicFeatureBars from '@/components/common/blocks/FullwidthTopicFeatureBars';
import Gallery from '@/components/common/blocks/Gallery';
import GenericIframe from '@/components/common/blocks/GenericIframe';
import HeroProduct from '@/components/common/blocks/HeroProduct';
import ImageOrVideo from '@/components/common/blocks/ImageOrVideo';
import ImageText from '@/components/common/blocks/ImageText';
import ImageTextHover from '@/components/common/blocks/ImageTextHover';
import ImageTitleFeature from '@/components/common/blocks/ImageTitleFeature';
import CtaBlock from '~/components/common/blocks/CtaBlock';
import InsuranceReimbursement from '@/components/common/blocks/InsuranceReimbursement';
import Jobs from '@/components/common/blocks/Jobs';
import LargeExerciseRoutineSlider from '@/components/common/blocks/LargeExerciseRoutineSlider';
import LargeStoryFeatureWithBars from '@/components/common/blocks/LargeStoryFeatureWithBars';
import LargeTopicFeature from '@/components/common/blocks/LargeTopicFeature';
import MediumTopicFeature from '@/components/common/blocks/MediumTopicFeature';
import MovingFullWidthImages from '@/components/common/blocks/MovingFullWidthImages';
import NewsletterSignupForm from '@/components/common/blocks/NewsletterSignupForm';
import NumberedImage from '@/components/common/blocks/NumberedImage';
import NumbersOnBlackBackground from '@/components/common/blocks/NumbersOnBlackBackground';
import ParallaxImages from '@/components/common/blocks/ParallaxImages';
import ProductGrid from '@/components/common/blocks/ProductGrid';
import ProductInformation from '@/components/common/blocks/ProductInformation';
import ProductReviews from '@/components/common/blocks/ProductReviews';
import ProductSlider from '@/components/common/blocks/ProductSlider';
import RelatedProducts from '@/components/common/blocks/RelatedProducts';
import RoutineCardsSlider from '@/components/common/blocks/RoutineCardsSlider';
import ShareThisArticle from '@/components/common/blocks/ShareThisArticle';
import SingleSocialMediaPost from '~/components/common/blocks/SingleSocialMediaPost';
import SmallInstruction from '@/components/common/blocks/SmallInstruction';
import SmallStoryFeaturesWithBars from '@/components/common/blocks/SmallStoryFeaturesWithBars';
import SmallTopicFeature from '@/components/common/blocks/SmallTopicFeature';
import SmallTopicFeatureBars from '@/components/common/blocks/SmallTopicFeatureBars';
import SmallTopicFeatureBox from '@/components/common/blocks/SmallTopicFeatureBox';
import SocialFeature from '@/components/common/blocks/SocialFeature';
import SplitContentSlider from '@/components/common/blocks/SplitContentSlider';
import StoryTeaserBlack from '@/components/common/blocks/StoryTeaserBlack';
import TeaserWithMultipleCtas from '@/components/common/blocks/TeaserWithMultipleCtas';
import Testimonial from '@/components/common/blocks/Testimonial';
import TextLeftImageRight from '@/components/common/blocks/TextLeftImageRight';
import Timeline from '@/components/common/blocks/Timeline';
import TwoPortraitImages from '@/components/common/blocks/TwoPortraitImages';
import Upsell from '@/components/common/blocks/Upsell';
import ValueRotation from '@/components/common/blocks/ValueRotation';
import VideoFeature from '@/components/common/blocks/VideoFeature';
import VideoRotation from '@/components/common/blocks/VideoRotation';
import VideoWithDescription from '@/components/common/blocks/VideoWithDescription';
import ProviderWrapper from '@/components/common/ProviderWrapper';
import guestComponent from '@/mixins/guestComponent';
// #endregion

export default defineNuxtComponent({
  components: {
    Accordion,
    AddOnTrainingChapter,
    AlsoBought,
    AppCta,
    ArticleChapter,
    ArticleRowSlide,
    AsSeenIn,
    CampaignGrid,
    CenteredContent,
    ChapterNav,
    CollectionInformation,
    LinksDisplay,
    SectionHeading,
    CompareContentSlider,
    CompareTable,
    ContactBlock,
    ContentList,
    ContentRecommendationSlide,
    ContentWithIcons,
    ConversionAreaOne, // we use a wrapper that optionally loads the product
    CopyText,
    CourseContent,
    CourseConversionBanner,
    CrossSell,
    Exercise,
    ExerciseRoutineCardsSliderWithText,
    ExpertDetailsListing,
    ExploreMore,
    FeaturedArticlesSlide,
    FeaturedExperts,
    FeatureList,
    FeaturesRotation,
    FeaturesWithIcons,
    FiftyFiftyTeaser,
    FindTraining,
    FixedBoxImages,
    FixedBoxTopicFeature,
    FormLink,
    FullWidthAutoplayVideo,
    FullWidthBrand,
    FullWidthTeaser,
    FullwidthTopicFeatureBars,
    Gallery,
    GenericIframe,
    HeroProduct,
    ImageOrVideo,
    ImageText,
    ImageTextHover,
    ImageTitleFeature,
    CtaBlock,
    InsuranceReimbursement,
    Jobs,
    LargeExerciseRoutineSlider,
    LargeStoryFeatureWithBars,
    LargeTopicFeature,
    MediumTopicFeature,
    MovingFullWidthImages,
    NewsletterSignupForm,
    NumberedImage,
    NumbersOnBlackBackground,
    ParallaxImages,
    ProductGrid,
    ProductInformation,
    ProductReviews,
    ProductSlider,
    RelatedProducts,
    RoutineCardsSlider,
    ShareThisArticle,
    SingleSocialMediaPost,
    SmallInstruction,
    SmallStoryFeaturesWithBars,
    SmallTopicFeature,
    SmallTopicFeatureBars,
    SmallTopicFeatureBox,
    SocialFeature,
    SplitContentSlider,
    StoryTeaserBlack,
    TeaserWithMultipleCtas,
    Testimonial,
    TextLeftImageRight,
    Timeline,
    TwoPortraitImages,
    Upsell,
    ValueRotation,
    VideoFeature,
    VideoRotation,
    VideoWithDescription,
    ProviderWrapper,
    WallsIoFeed: GenericIframe,
  },

  mixins: [guestComponent],

  props: {
    socialMediaAccounts: {
      type: Array,
      default: () => [],
    },
    gridBlocks: {
      type: Array,
      default: () => [],
    },
    collection: {
      type: Object,
      default: () => null,
    },
    entry: {
      type: Object,
      default: () => null,
    },
    product: {
      type: Object,
      default: () => null,
    },
    addOn: {
      type: Object,
      default: () => null,
    },
    events: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: null,
    },
  },

  computed: {
    entryId() {
      return this.entry?.id || null;
    },
    blocks() {
      return this.entry?.contentBlocks || [];
    },
    isProductPage() {
      return this.entry?.typeHandle === 'products';
    },
    agreementCategory() {
      return this.entry?.agreementCategory;
    },

    blocksWithIteratedChapter() {
      let chapterCount = 1;

      return this.blocks.map((block) => {
        if (block.typeHandle === 'articleChapter') {
          block.index = chapterCount++;
          block.chapterId = 'chapter-' + block.id;
        }

        return block;
      });
    },
  },
});
</script>
